import { Fragment, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Undo2, ChevronDown, ChevronRight, PackageCheck, MapPin, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ShippingLabelButton } from "@/components/ShippingLabelButton";

const OK = ["COMPLETED", "succeeded"];

function itemsSummary(items) {
  if (!items || !items.length) return "—";
  return items.map((i) => `${i.quantity}× ${i.name}`).join(", ");
}

function StatusPill({ status }) {
  const ok = OK.includes(status);
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border ${ok ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : "bg-destructive/15 text-destructive border-destructive/40"}`}>
      {(status || "unknown").toUpperCase()}
    </span>
  );
}

const FULFILL_STYLES = {
  fulfilled: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  partial: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  unfulfilled: "bg-muted text-muted-foreground border-border",
};

function FulfillmentPill({ status, testid }) {
  return (
    <span data-testid={testid} className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border ${FULFILL_STYLES[status] || FULFILL_STYLES.unfulfilled}`}>
      {(status || "unfulfilled").toUpperCase()}
    </span>
  );
}

export default function TransactionsTable({ txns, onRefresh }) {
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null);

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const call = async (path, body) => {
    setBusy(path);
    try {
      await api.post(path, body || {});
      await onRefresh?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const toggleWhole = (t) => {
    const done = t.fulfillment_status === "fulfilled";
    call(`/orders/${t.id}/${done ? "unfulfill" : "fulfill"}`);
  };

  const toggleItem = (t, index, fulfilled) =>
    call(`/orders/${t.id}/${fulfilled ? "unfulfill" : "fulfill"}`, { item_index: index });

  return (
    <Card className="border-border/70 overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">When</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Provider</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Customer</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Products</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Amount</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Payment</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Fulfillment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="transactions-table-body">
            {txns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                  No transactions yet.
                </TableCell>
              </TableRow>
            ) : (
              txns.map((t) => {
                const hasItems = (t.items || []).length > 0;
                const expandable = hasItems || !!t.shipping;
                const done = t.fulfillment_status === "fulfilled";
                return (
                  <Fragment key={t.id}>
                    <TableRow className="border-border/60" data-testid={`txn-row-${t.id}`}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {new Date(t.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <span className={`font-mono text-xs ${t.provider === "square" ? "text-emerald-400" : "text-indigo-400"}`}>
                          {t.provider}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {t.customer_email || t.customer_name || "—"}
                      </TableCell>
                      <TableCell className="text-xs max-w-[240px] text-muted-foreground" data-testid={`txn-items-${t.id}`}>
                        <button
                          type="button"
                          onClick={() => expandable && toggleExpand(t.id)}
                          className={`flex items-center gap-1 max-w-full ${expandable ? "hover:text-foreground cursor-pointer" : "cursor-default"}`}
                          data-testid={`fulfill-expand-${t.id}`}
                          title={itemsSummary(t.items)}
                        >
                          {expandable && (expanded[t.id] ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />)}
                          <span className="truncate">{itemsSummary(t.items)}</span>
                        </button>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        ${Number(t.amount).toFixed(2)} {t.currency}
                      </TableCell>
                      <TableCell><StatusPill status={t.payment_status} /></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FulfillmentPill status={t.fulfillment_status} testid={`txn-fulfillment-${t.id}`} />
                          {OK.includes(t.payment_status) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              data-testid={`fulfill-toggle-${t.id}`}
                              disabled={busy?.startsWith(`/orders/${t.id}/`)}
                              onClick={() => toggleWhole(t)}
                              className="h-7 gap-1 text-[11px]"
                            >
                              {done ? (<><Undo2 className="h-3 w-3" /> Unfulfill</>) : (<><Check className="h-3 w-3" /> Fulfilled</>)}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandable && expanded[t.id] && (
                      <TableRow className="border-border/60 bg-muted/20" data-testid={`txn-items-expand-${t.id}`}>
                        <TableCell colSpan={7} className="py-3 space-y-3">
                          {hasItems && (
                            <div className="flex flex-wrap gap-2">
                              {t.items.map((it) => (
                                <button
                                  key={it.index}
                                  type="button"
                                  data-testid={`fulfill-item-${t.id}-${it.index}`}
                                  onClick={() => toggleItem(t, it.index, it.fulfilled)}
                                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-mono transition-colors ${it.fulfilled ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : "bg-secondary text-muted-foreground border-border hover:text-foreground"}`}
                                >
                                  {it.fulfilled ? <Check className="h-3 w-3" /> : <PackageCheck className="h-3 w-3" />}
                                  {it.quantity}× {it.name}
                                </button>
                              ))}
                            </div>
                          )}
                          {t.shipping && (
                            <div className="flex items-start gap-2 text-xs font-mono text-muted-foreground" data-testid={`txn-shipping-${t.id}`}>
                              <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                              <span>
                                <span className="text-foreground">Ship to:</span>{" "}
                                {t.shipping.name ? `${t.shipping.name}, ` : ""}
                                {t.shipping.line1}
                                {t.shipping.line2 ? `, ${t.shipping.line2}` : ""}, {t.shipping.city}, {t.shipping.state} {t.shipping.postal_code}, {t.shipping.country}
                                {t.shipping.phone ? ` · ${t.shipping.phone}` : ""}
                              </span>
                            </div>
                          )}
                          {t.shipping_rate && (
                            <div className="flex items-start gap-2 text-xs font-mono text-muted-foreground" data-testid={`txn-shipping-method-${t.id}`}>
                              <Truck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                              <span>
                                <span className="text-foreground">Customer chose:</span>{" "}
                                {[t.shipping_rate.carrier, t.shipping_rate.service].filter(Boolean).join(" ") || t.shipping_rate.servicelevel_token || "Shipping"}
                                {t.shipping_rate.amount != null ? ` · $${Number(t.shipping_rate.amount).toFixed(2)}` : ""}
                                {t.tax != null ? ` · Tax $${Number(t.tax).toFixed(2)}` : ""}
                              </span>
                            </div>
                          )}
                          {t.shipping && OK.includes(t.payment_status) && (
                            <ShippingLabelButton order={t} />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
